import PinIcon from "@atlaskit/icon/core/pin";
import CrossIcon from "@atlaskit/icon/core/cross";
import SearchIcon from "@atlaskit/icon/core/search";
import DragHandleVerticalIcon from "@atlaskit/icon/core/drag-handle-vertical";
import LayoutThreeColumnsIcon from "@atlaskit/icon/core/layout-three-columns";
import {useFlags} from "@atlaskit/flag";
import {token} from "@atlaskit/tokens";
import type {DragEndEvent} from "@dnd-kit/core";
import {closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {Popover} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {createContext, useContext, useState, type ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {BottomDrawer} from "~/components/BottomDrawer";
import {
    type KanbanColumnConfig,
    orderedVisibleColumnIdsWithActionsLast,
    TABLE_ACTIONS_COLUMN_ID,
    type TableColumnConfig,
} from "~/components/ottopilotDataViewer/columnConfiguration";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeCheckbox} from "@vesta/ananke/components/anankeCheckbox";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

/** Column configuration needs only view metadata and column state, including in admin viewers. */
export type ConfigureColumnsController<T extends {id: Uuid}> = Pick<OttopilotDataViewerController<T>,
    "columns" | "currentViewId" | "views" | "tableColumnConfig" | "setTableColumnConfig" | "kanbanColumnConfig" | "setKanbanColumnConfig"
>;

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";
const MAX_TABLE_PINS = 3;
const MAX_TABLE_PINS_MOBILE = 1;
const MAX_KANBAN_PINS = 1;

// ─── Types ───────────────────────────────────────────────────────────────────

type ColumnConfigLineItem = {
    id: string;
    displayName: string;
    isVisible: boolean;
    isPinned: boolean;
    /** Background color for kanban columns (not used for table) */
    background?: string;
    /** Text color for kanban column labels */
    textColor?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeTableOrderForPinnedLeft(items: Array<{id: string; isPinned: boolean}>): Array<string> {
    const pinned = items.filter((item) => item.isPinned).map((item) => item.id);
    const unpinned = items.filter((item) => item.isPinned === false).map((item) => item.id);
    return [...pinned, ...unpinned];
}

function buildTableLineItems<T extends {id: Uuid}>(
    controller: ConfigureColumnsController<T>,
    currentView: {visibleColumns: Array<string>},
): Array<ColumnConfigLineItem> {
    const config = controller.tableColumnConfig;
    const orderedIdsRaw = config !== null ? config.orderedVisibleColumnIds : currentView.visibleColumns;
    const orderedIds = orderedVisibleColumnIdsWithActionsLast(
        orderedIdsRaw,
        orderedIdsRaw.includes(TABLE_ACTIONS_COLUMN_ID),
    );
    const pinnedIds = config !== null ? config.pinnedLeftColumnIds : [];

    // All columns available - start with the ordered visible set, then append hidden ones
    const allColumnIds = controller.columns.lineItems.filter((c) => c.id !== "__checkbox__").map((c) => c.id);

    const visibleSet = new Set(orderedIds);

    // Build ordered list: ordered visible columns first, then remaining
    const orderedAll: Array<string> = [
        ...orderedIds.filter((id) => allColumnIds.includes(id)),
        ...allColumnIds.filter((id) => visibleSet.has(id) === false),
    ];

    return orderedAll.map((id) => {
        const col = controller.columns.lineItems.find((c) => c.id === id);
        return {
            id: id,
            displayName: col !== undefined ? col.displayName : id,
            isVisible: visibleSet.has(id),
            isPinned: pinnedIds.includes(id),
        };
    });
}

function buildKanbanLineItems(
    columnConfig: Array<{id: Uuid; displayName: string; background: string; textColor: string}>,
    kanbanColumnConfig: KanbanColumnConfig | null,
): Array<ColumnConfigLineItem> {
    const hiddenIds = kanbanColumnConfig !== null ? new Set(kanbanColumnConfig.hiddenColumnIds) : new Set<Uuid>();
    const pinnedId = kanbanColumnConfig !== null ? kanbanColumnConfig.pinnedColumnId : null;

    const orderedIds =
        kanbanColumnConfig !== null
            ? [
                  ...kanbanColumnConfig.orderedColumnIds.filter((id) => columnConfig.some((c) => c.id === id)),
                  ...columnConfig
                      .map((c) => c.id)
                      .filter((id) => kanbanColumnConfig.orderedColumnIds.includes(id) === false),
              ]
            : columnConfig.map((c) => c.id);

    return orderedIds.map((id) => {
        const col = columnConfig.find((c) => c.id === id);
        return {
            id: id,
            displayName: col !== undefined ? col.displayName : id,
            isVisible: hiddenIds.has(id) === false,
            isPinned: pinnedId === id,
            background: col?.background,
            textColor: col?.textColor,
        };
    });
}

function buildInitialEphemeralItems<T extends {id: Uuid}>(
    controller: ConfigureColumnsController<T>,
    currentView: ConfigureColumnsController<T>["views"][number] | null | undefined,
    isTable: boolean,
    isKanban: boolean,
): Array<ColumnConfigLineItem> {
    if (isTable && currentView !== null && currentView !== undefined && currentView.viewType === ViewType.table) {
        return buildTableLineItems(controller, currentView);
    }
    if (isKanban && currentView !== null && currentView !== undefined && currentView.viewType === ViewType.kanbanBoard) {
        return buildKanbanLineItems(currentView.columnConfig, controller.kanbanColumnConfig);
    }
    return [];
}

type ConfigureColumnsEmbeddedParts = {
    panel: ReactNode;
    footer: ReactNode;
};

const ConfigureColumnsEmbeddedContext = createContext<ConfigureColumnsEmbeddedParts | null>(null);

export function ConfigureColumnsEmbeddedScope<T extends {id: Uuid}>({
    active,
    controller,
    onEmbeddedClose,
    onEmbeddedCancel,
    children,
}: {
    active: boolean;
    controller: ConfigureColumnsController<T>;
    onEmbeddedClose?: () => void;
    onEmbeddedCancel?: () => void;
    children: ReactNode;
}) {
    if (active === false) {
        return children;
    }

    return (
        <ConfigureColumnsComponent
            controller={controller}
            embeddedOnly={true}
            embeddedActive={true}
            embeddedUseContextProvider={true}
            onEmbeddedClose={onEmbeddedClose}
            onEmbeddedCancel={onEmbeddedCancel}
            embeddedProviderChildren={children}
        />
    );
}

export function ConfigureColumnsEmbeddedPanel() {
    const parts = useContext(ConfigureColumnsEmbeddedContext);
    return parts?.panel ?? null;
}

export function ConfigureColumnsEmbeddedFooter() {
    const parts = useContext(ConfigureColumnsEmbeddedContext);
    return parts?.footer ?? null;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ConfigureColumnsComponent<T extends {id: Uuid}>({
    controller,
    embeddedOnly = false,
    embeddedActive = false,
    onEmbeddedClose,
    onEmbeddedCancel,
    embeddedUseContextProvider = false,
    embeddedProviderChildren,
}: {
    controller: ConfigureColumnsController<T>;
    /** Renders panel + footer only (no trigger or drawer) for nesting inside another bottom sheet. */
    embeddedOnly?: boolean;
    embeddedActive?: boolean;
    /** Called after Apply in embedded mode (and when closing standalone popup). */
    onEmbeddedClose?: () => void;
    /** Called when Cancel is pressed in embedded mode; defaults to `onEmbeddedClose`. */
    onEmbeddedCancel?: () => void;
    /** Provides panel/footer via context so a parent bottom drawer can use a fixed footer slot. */
    embeddedUseContextProvider?: boolean;
    embeddedProviderChildren?: ReactNode;
}) {
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const {showFlag} = useFlags();

    const currentView =
        controller.currentViewId === null
            ? null
            : controller.views.find((view) => view.id === controller.currentViewId);

    const isTable = currentView !== null && currentView !== undefined && currentView.viewType === ViewType.table;
    const isKanban = currentView !== null && currentView !== undefined && currentView.viewType === ViewType.kanbanBoard;
    const isEnabled = isTable || isKanban;

    const maxPins = isTable ? (isMobile ? MAX_TABLE_PINS_MOBILE : MAX_TABLE_PINS) : MAX_KANBAN_PINS;

    const [opened, setOpened] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [ephemeralItems, setEphemeralItems] = useState<Array<ColumnConfigLineItem>>(() => {
        if (embeddedOnly === true && embeddedActive === true) {
            return buildInitialEphemeralItems(controller, currentView, isTable, isKanban);
        }
        return [];
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 6}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    const initializePanel = () => {
        if (isEnabled === false) {
            return;
        }
        setSearchQuery("");
        if (isTable && currentView !== null && currentView !== undefined && currentView.viewType === ViewType.table) {
            setEphemeralItems(buildTableLineItems(controller, currentView));
        } else if (
            isKanban &&
            currentView !== null &&
            currentView !== undefined &&
            currentView.viewType === ViewType.kanbanBoard
        ) {
            setEphemeralItems(buildKanbanLineItems(currentView.columnConfig, controller.kanbanColumnConfig));
        }
    };

    const openPopup = () => {
        initializePanel();
        setOpened(true);
    };

    const closePopup = () => {
        setOpened(false);
        onEmbeddedClose?.();
    };

    const applyChanges = () => {
        if (isTable) {
            const visibleItems = ephemeralItems.filter((item) => item.isVisible);
            const actionsVisible = visibleItems.some(
                (item) => item.id === TABLE_ACTIONS_COLUMN_ID && item.isVisible,
            );
            const orderedVisibleColumnIds = orderedVisibleColumnIdsWithActionsLast(
                normalizeTableOrderForPinnedLeft(
                    visibleItems.map((item) => ({id: item.id, isPinned: item.isPinned})),
                ),
                actionsVisible,
            );
            const newConfig: TableColumnConfig = {
                revisionId: generateUuid(),
                orderedVisibleColumnIds: orderedVisibleColumnIds,
                pinnedLeftColumnIds: visibleItems
                    .filter((item) => item.isPinned)
                    .map((item) => item.id)
                    .filter((id) => id !== TABLE_ACTIONS_COLUMN_ID),
            };
            controller.setTableColumnConfig(newConfig);
        } else if (isKanban) {
            const newConfig: KanbanColumnConfig = {
                revisionId: generateUuid(),
                orderedColumnIds: ephemeralItems.map((item) => item.id as Uuid),
                hiddenColumnIds: ephemeralItems
                    .filter((item) => item.isVisible === false)
                    .map((item) => item.id as Uuid),
                pinnedColumnId: (ephemeralItems.find((item) => item.isPinned)?.id as Uuid | undefined) ?? null,
            };
            controller.setKanbanColumnConfig(newConfig);
        }
        closePopup();
    };

    const toggleVisibility = (id: string) => {
        setEphemeralItems((prev) =>
            prev.map((item) => {
                if (item.id !== id) {
                    return item;
                }
                // When hiding a column, also remove its pin
                return {
                    ...item,
                    isVisible: item.isVisible === false,
                    isPinned: item.isVisible === true ? false : item.isPinned,
                };
            }),
        );
    };

    const togglePin = (id: string) => {
        const item = ephemeralItems.find((i) => i.id === id);
        if (item === undefined) {
            return;
        }

        if (item.isPinned === true) {
            // Unpin
            setEphemeralItems((prev) => {
                const next = prev.map((i) => (i.id === id ? {...i, isPinned: false} : i));
                const normalizedIds = normalizeTableOrderForPinnedLeft(next);
                return normalizedIds
                    .map((normalizedId) => next.find((entry) => entry.id === normalizedId))
                    .filter((entry): entry is ColumnConfigLineItem => entry !== undefined);
            });
            return;
        }

        const currentPinCount = ephemeralItems.filter((i) => i.isPinned).length;
        if (currentPinCount >= maxPins) {
            showFlag({
                id: "pin-limit-error",
                title: "Pin limit reached",
                description: isTable
                    ? `Maximum of ${maxPins} column${maxPins === 1 ? "" : "s"} can be pinned. Unpin a column to add a new one.`
                    : `Maximum of ${MAX_KANBAN_PINS} column can be pinned. Unpin the current column first.`,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
            return;
        }

        setEphemeralItems((prev) => {
            const next = prev.map((i) => (i.id === id ? {...i, isPinned: true} : i));
            const normalizedIds = normalizeTableOrderForPinnedLeft(next);
            return normalizedIds
                .map((normalizedId) => next.find((entry) => entry.id === normalizedId))
                .filter((entry): entry is ColumnConfigLineItem => entry !== undefined);
        });
    };
    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        if (over !== null && over !== undefined && active.id !== over.id) {
            setEphemeralItems((prev) => {
                const oldIndex = prev.findIndex((i) => i.id === active.id);
                const newIndex = prev.findIndex((i) => i.id === over.id);
                const moved = arrayMove(prev, oldIndex, newIndex);
                // AG Grid style: pinned columns remain grouped as a left section.
                const normalizedIds = normalizeTableOrderForPinnedLeft(moved);
                return normalizedIds
                    .map((normalizedId) => moved.find((entry) => entry.id === normalizedId))
                    .filter((entry): entry is ColumnConfigLineItem => entry !== undefined);
            });
        }
    };

    const filteredItems =
        searchQuery.trim() === ""
            ? ephemeralItems
            : ephemeralItems.filter((item) => item.displayName.toLowerCase().includes(searchQuery.toLowerCase()));

    const selectedCount = ephemeralItems.filter((item) => item.isVisible).length;
    const totalCount = ephemeralItems.length;
    const allSelected = totalCount > 0 && selectedCount === totalCount;
    const someSelected = selectedCount > 0 && selectedCount < totalCount;

    const onConfigureColumnsTriggerClick = () => {
        if (opened === false) {
            openPopup();
        } else {
            closePopup();
        }
    };

    const triggerButton =
        isMobile === true ? (
            <AnankeIconButton
                type="button"
                appearance="default"
                icon={LayoutThreeColumnsIcon}
                label="Configure columns"
                aria-expanded={opened}
                isDisabled={isEnabled === false}
                isSelected={opened}
                onClick={onConfigureColumnsTriggerClick}
            />
        ) : (
            <AnankeButton
                type="button"
                iconBefore={() => <LayoutThreeColumnsIcon label="" />}
                onClick={onConfigureColumnsTriggerClick}
                isDisabled={isEnabled === false}
                isSelected={opened}
            >
                Columns
            </AnankeButton>
        );

    const panelContent = (
        <>
            <div className="w-full grid grid-cols-1 gap-y-ds-space-100">
                {/* Search */}
                <div className="pr-ds-space-100 pt-ds-space-100">
                    <AnankeTextField
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.currentTarget.value);
                        }}
                        elemBeforeInput={
                            <div className="pl-ds-space-100 grid place-items-center">
                                <SearchIcon label="" />
                            </div>
                        }
                    />
                </div>

                {/* Select all toggle */}
                <div className="">
                    <SelectAllRow
                        selectedCount={selectedCount}
                        totalCount={totalCount}
                        allSelected={allSelected}
                        someSelected={someSelected}
                        onToggle={() => {
                            if (allSelected) {
                                setEphemeralItems((prev) =>
                                    prev.map((item) => ({...item, isVisible: false, isPinned: false})),
                                );
                            } else {
                                setEphemeralItems((prev) => prev.map((item) => ({...item, isVisible: true})));
                            }
                        }}
                    />
                </div>

                {/* Column list */}
                <div className="pb-ds-space-050">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={filteredItems.map((i) => i.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="grid grid-cols-1">
                                {filteredItems.map((item) => (
                                    <ColumnRow
                                        key={item.id}
                                        item={item}
                                        onToggleVisibility={toggleVisibility}
                                        onTogglePin={togglePin}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
        </>
    );

    const footerContent = (
        <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                <AnankeButton
                    type="button"
                    appearance="subtle"
                    onClick={() => {
                        if (embeddedOnly === true) {
                            (onEmbeddedCancel ?? onEmbeddedClose)?.();
                            return;
                        }
                        closePopup();
                    }}
                >
                    Cancel
                </AnankeButton>
                <AnankeButton
                    type="button"
                    appearance="primary"
                    onClick={applyChanges}
                >
                    Apply
                </AnankeButton>
        </div>
    );

    if (embeddedOnly === true) {
        if (embeddedActive === false || isEnabled === false) {
            return null;
        }
        if (embeddedUseContextProvider === true) {
            return (
                <ConfigureColumnsEmbeddedContext.Provider value={{panel: panelContent, footer: footerContent}}>
                    {embeddedProviderChildren}
                </ConfigureColumnsEmbeddedContext.Provider>
            );
        }
        return (
            <>
                {panelContent}
                {footerContent}
            </>
        );
    }

    if (isMobile === true) {
        return (
            <>
                {triggerButton}
                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title="Configure columns"
                    zIndex={100}
                    fitContentOnOpen={true}
                    footer={footerContent}
                >
                    {panelContent}
                </BottomDrawer>
            </>
        );
    }

    return (
        <Popover
            position="bottom-start"
            width={320}
            opened={opened}
            onChange={(newValue) => {
                if (newValue === true) {
                    openPopup();
                } else {
                    closePopup();
                }
            }}
        >
            <Popover.Target>{triggerButton}</Popover.Target>
            <Popover.Dropdown
                p={0}
                style={{
                    borderRadius: token("radius.large"),
                    border: `1px solid ${token("color.border")}`,
                    boxShadow: token("elevation.shadow.overlay"),
                    overflow: "hidden",
                }}
            >
                <div className="w-full grid grid-cols-1">
                    {/* Header */}
                    <div className="px-ds-space-150 py-ds-space-100 grid grid-cols-[minmax(0,1fr)_auto] items-center border-b border-ds-border">
                        <span className="text-sm font-semibold text-ds-text">Configure columns</span>
                        <AnankeIconButton
                            type="button"
                            icon={CrossIcon}
                            label="Close"
                            appearance="subtle"
                            spacing="compact"
                            onClick={closePopup}
                        />
                    </div>

                    {/* Scrollable body */}
                    <div className="h-[18rem] overflow-y-auto md:px-ds-space-100">{panelContent}</div>

                    {/* Footer */}
                    {footerContent}
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}

// ─── Select-all row ───────────────────────────────────────────────────────────

function SelectAllRow({
    selectedCount,
    totalCount,
    allSelected,
    someSelected,
    onToggle,
}: {
    selectedCount: number;
    totalCount: number;
    allSelected: boolean;
    someSelected: boolean;
    onToggle: () => void;
}) {
    return (
        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-ds-space-100 items-center px-ds-space-050 py-ds-space-075 select-none">
            <AnankeCheckbox
                isChecked={allSelected || someSelected}
                isIndeterminate={someSelected}
                onChange={onToggle}
            />
            <span className="text-sm font-medium text-ds-text">
                {selectedCount}/{totalCount} Selected
            </span>
        </div>
    );
}

// ─── Column row ───────────────────────────────────────────────────────────────

function ColumnRow({
    item,
    onToggleVisibility,
    onTogglePin,
}: {
    item: ColumnConfigLineItem;
    onToggleVisibility: (id: string) => void;
    onTogglePin: (id: string) => void;
}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: item.id});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 100 : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={twJoin(
                "grid grid-cols-[2rem_2rem_minmax(0,1fr)_2rem] md:grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center",
                "gap-x-ds-space-100 md:gap-x-ds-space-075",
                "py-ds-space-100 md:py-ds-space-075 px-ds-space-075 md:px-ds-space-050",
                "hover:bg-ds-background-neutral-subtle-hovered rounded",
                isDragging ? "bg-ds-background-selected" : "",
            )}
        >
            <button
                type="button"
                className="grid h-8 w-8 md:h-auto md:w-auto place-items-center rounded text-ds-icon-subtle cursor-grab active:cursor-grabbing touch-none"
                {...attributes}
                {...listeners}
                aria-label="Drag to reorder"
            >
                <DragHandleVerticalIcon label="" />
            </button>
            {/* Visibility checkbox */}
            <label className="grid h-8 w-8 md:h-auto md:w-auto place-items-center cursor-pointer">
                <input
                    type="checkbox"
                    checked={item.isVisible}
                    onChange={() => {
                        onToggleVisibility(item.id);
                    }}
                    className="w-4 h-4 accent-ds-icon-brand cursor-pointer"
                />
            </label>

            {/* Column name (with optional kanban color dot) */}
            <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-ds-space-075 items-center min-w-0">
                {item.background !== undefined && (
                    <span
                        className="w-3 h-3 rounded-full flex-none"
                        style={{background: item.background}}
                    />
                )}
                <span
                    className={twJoin(
                        "text-sm truncate",
                        item.isVisible ? "text-ds-text" : "text-ds-text-subtlest",
                    )}
                >
                    {item.displayName}
                </span>
            </div>

            {/* Pin button */}
            <button
                type="button"
                className={twJoin(
                    "grid place-items-center p-[0.25rem] rounded transition-colors",
                    item.isVisible === false
                        ? "opacity-30 cursor-not-allowed"
                        : item.isPinned
                          ? "text-ds-icon-brand bg-ds-background-selected hover:bg-ds-background-selected-hovered"
                          : "text-ds-icon-subtle hover:text-ds-icon hover:bg-ds-background-neutral-hovered",
                )}
                onClick={() => {
                    if (item.isVisible) {
                        onTogglePin(item.id);
                    }
                }}
                disabled={item.isVisible === false}
                aria-label={item.isPinned ? "Unpin column" : "Pin column to left"}
                title={item.isPinned ? "Unpin column" : "Pin column to left"}
            >
                <PinIcon label="" />
            </button>
        </div>
    );
}
